import { z } from "zod";
import {
  isGroup,
  nonRoutableEmployeePerformedActivityOccurrenceOrGroupSchema,
} from "./activities";
import { isRoute, routeSchema } from "./routes";
import { scheduledShiftSchema } from "./shifts";
import type { IUnenrichedShiftItem } from "./shiftWithContents.unenriched";

// A shift holds routes, which carry routable work, alongside standalone
// non-routable occurrences and groups.
export const shiftWithContentsSchema = z.object({
  shift: scheduledShiftSchema,
  items: z.array(
    z.union([
      routeSchema,
      nonRoutableEmployeePerformedActivityOccurrenceOrGroupSchema,
    ]),
  ),
});
export type IShiftWithContents = z.infer<typeof shiftWithContentsSchema>;
export type IShiftItem = IShiftWithContents["items"][number];

type AnyShiftItem = IShiftItem | IUnenrichedShiftItem;

export const shiftItemKindSchema = z.enum(["route", "group", "occurrence"]);

export type ClassifiedShiftItem<T extends AnyShiftItem = IShiftItem> =
  | {
      kind: typeof shiftItemKindSchema.enum.route;
      item: Extract<T, { visits: unknown }>;
    }
  | {
      kind: typeof shiftItemKindSchema.enum.group;
      item: Extract<T, { occurrences: unknown }>;
    }
  | {
      kind: typeof shiftItemKindSchema.enum.occurrence;
      item: Extract<T, { activityId: unknown }>;
    };

// Shift items carry no shared discriminator, so each kind is recognised by a
// field only it has. Classifying here, once, hands consumers a discriminated
// union to switch on exhaustively, so a new kind of shift item is a type error
// at every consumer instead of falling silently into whichever branch a
// hand-written check leaves open.
export function classifyShiftItem<T extends AnyShiftItem>(
  item: T,
): ClassifiedShiftItem<T>;
export function classifyShiftItem(
  item: AnyShiftItem,
): ClassifiedShiftItem<AnyShiftItem> {
  if (isRoute(item)) {
    return { kind: shiftItemKindSchema.enum.route, item };
  }
  if (isGroup(item)) {
    return { kind: shiftItemKindSchema.enum.group, item };
  }
  if ("activityId" in item) {
    return { kind: shiftItemKindSchema.enum.occurrence, item };
  }
  throw item satisfies never;
}
